import { useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * WarmLightbox — accessible, keyboard- and swipe-navigable image lightbox.
 *
 * WHY: The original gallery lightbox opened a single image with no way to move
 * between photos, no keyboard support, and no focus management. Aspira's site has
 * no lightbox at all. This component gives Dawit Solomon a best-in-class viewer:
 *   • Arrow keys / on-screen arrows / touch-swipe to move between images
 *   • Esc or backdrop click to close, with focus returned to the trigger
 *   • "n / total" counter and caption
 *   • Body scroll lock + focus trap while open (accessibility)
 *
 * Brand-safe: uses existing theme tokens (--card, --accent, --radius, shadows).
 * It is presentation-only and holds no data of its own, so it is reusable for the
 * gallery grid and for "view the original handwritten note" on testimonials.
 */

export interface LightboxItem {
  src: string;
  title?: string;
  category?: string;
}

interface WarmLightboxProps {
  items: LightboxItem[];
  /** Index of the currently shown item, or null when closed. */
  index: number | null;
  onClose: () => void;
  onIndexChange: (next: number) => void;
}

export function WarmLightbox({ items, index, onClose, onIndexChange }: WarmLightboxProps) {
  const isOpen = index !== null;
  const touchStartX = useRef<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const goPrev = useCallback(() => {
    if (index === null) return;
    onIndexChange((index - 1 + items.length) % items.length);
  }, [index, items.length, onIndexChange]);

  const goNext = useCallback(() => {
    if (index === null) return;
    onIndexChange((index + 1) % items.length);
  }, [index, items.length, onIndexChange]);

  // Keyboard navigation: Esc closes, arrows move.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, goPrev, goNext]);

  // Lock body scroll while open, move focus to the close button, and RETURN focus
  // to the element that opened the lightbox on close (WCAG 2.4.3 focus order).
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = original;
      opener?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen || index === null) return null;
  const item = items[index];
  const showNav = items.length > 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/85 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label={item.title ? `Image: ${item.title}` : "Image viewer"}
      onClick={onClose}
    >
      {/* Close */}
      <button
        ref={closeButtonRef}
        onClick={onClose}
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        aria-label="Close viewer"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Counter */}
      {showNav && (
        <span className="absolute left-1/2 top-5 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white/90">
          {index + 1} / {items.length}
        </span>
      )}

      {/* Prev / Next */}
      {showNav && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent md:left-6"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-7 w-7" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent md:right-6"
            aria-label="Next image"
          >
            <ChevronRight className="h-7 w-7" />
          </button>
        </>
      )}

      {/* Image card */}
      <figure
        className="relative max-h-[90vh] w-full max-w-4xl rounded-3xl bg-card p-3 shadow-[var(--shadow-card)]"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchStartX.current;
          if (Math.abs(dx) > 50) { if (dx > 0) goPrev(); else goNext(); }
          touchStartX.current = null;
        }}
      >
        <div className="overflow-hidden rounded-2xl">
          <img
            src={item.src}
            alt={item.title || ""}
            className="mx-auto h-auto max-h-[78vh] w-full object-contain"
          />
        </div>
        {(item.title || item.category) && (
          <figcaption className="mt-3 text-center">
            {item.title && <span className="text-lg font-medium text-foreground">{item.title}</span>}
            {item.category && <p className="text-sm text-muted-foreground">{item.category}</p>}
          </figcaption>
        )}
      </figure>
    </div>
  );
}
